import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CalendarIcon,
  CurrencyDollarIcon,
  ClockIcon,
  TruckIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  StarIcon,
  ExclamationCircleIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalculatorIcon,
  TagIcon,
  EyeIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  RectangleStackIcon,
  TableCellsIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import {
  fetchAmbulanceProfile,
  fetchAmbulanceBookings,
  fetchVendorFinancialOverview,
  fetchLocationAnalytics,
  fetchVehicleUsage,
  fetchAmbulanceInvoiceBlob,
} from '../../../services/MasterAdmin/ambulanceService';
import { getFeedbacks } from '../../../services/MasterAdmin/feedbackService';
import { toggleVendorStatus, fetchVendorCommissionRate, updateVendorCommissionRate } from '../../../services/MasterAdmin/vendorService';
import { AmbulanceVendor, AmbulanceBooking, BookingStats } from '../../../models/MasterAdmin/ambulanceBookings';
import type { FeedbackRow } from '../../../services/MasterAdmin/feedbackService';
import type { VendorFinancialOverview } from '../../../services/MasterAdmin/ambulanceService';
import type { LocationAnalyticsResponse, VehicleUsageResponse } from '../../../services/MasterAdmin/ambulanceService';
import FilePreview from '../../../components/MasterAdmin/FilePreview';
import { format } from 'date-fns';
import VendorBankDetailsModal from '../../../components/MasterAdmin/VendorBankDetailsModal';
import AmbulanceVendorDocumentUploadModal from '../../../components/MasterAdmin/AmbulanceVendorDocumentUploadModal';
import toast from 'react-hot-toast';
import type { VendorDocument } from '../../../models/MasterAdmin/ambulanceBookings';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: string;
  trendValue?: string;
  description?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, trend, trendValue, description }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6 hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-700">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
        <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-2">{value}</p>
        {trend && (
          <p className={`text-sm mt-2 ${trend === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {trendValue}
          </p>
        )}
        {description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>
        )}
      </div>
      <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">{icon}</div>
    </div>
  </div>
);

const AMBULANCE_INVOICE_ELIGIBLE_STATUSES = new Set([
  'payment_completed',
  'assigned',
  'accepted',
  'arrived',
  'started',
  'completed',
  'delivered',
]);

const AmbulanceVendorDashboard: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const [ambulanceData, setAmbulanceData] = useState<AmbulanceVendor | null>(null);
  const [bookings, setBookings] = useState<AmbulanceBooking[]>([]);
  const [bookingStats, setBookingStats] = useState<BookingStats | null>(null);
  const [pagination, setPagination] = useState<{ total: number; page: number; limit: number; totalPages: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [isCommissionModalOpen, setIsCommissionModalOpen] = useState(false);
  const [commissionRateInput, setCommissionRateInput] = useState<string>('');
  const [referralCommissionInput, setReferralCommissionInput] = useState<string>('');
  const [commissionLoading, setCommissionLoading] = useState(false);
  const [commissionSaving, setCommissionSaving] = useState(false);
  const [commissionError, setCommissionError] = useState<string | null>(null);
  const [isBankDetailsModalOpen, setIsBankDetailsModalOpen] = useState(false);
  const [isDocumentUploadOpen, setIsDocumentUploadOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [bookingsLoading, setBookingsLoading] = useState(false);

  const [financialOverview, setFinancialOverview] = useState<VendorFinancialOverview | null>(null);
  const [locationAnalytics, setLocationAnalytics] = useState<LocationAnalyticsResponse | null>(null);
  const [vehicleUsage, setVehicleUsage] = useState<VehicleUsageResponse | null>(null);
  const [vendorFeedback, setVendorFeedback] = useState<FeedbackRow[]>([]);

  const loadBookings = useCallback(async (page: number = 1) => {
    if (!vendorId) return;
    setBookingsLoading(true);
    try {
      const res = await fetchAmbulanceBookings(vendorId, {
        page,
        limit: 10,
        search: searchText || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      });
      setBookings(Array.isArray(res?.bookings) ? res.bookings : []);
      setBookingStats(res?.stats ?? null);
      if (res?.pagination) setPagination(res.pagination);
    } catch (err) {
      console.error('Error loading bookings:', err);
    } finally {
      setBookingsLoading(false);
    }
  }, [vendorId, searchText, statusFilter]);

  useEffect(() => {
    if (!vendorId) return;
    const loadData = async () => {
      try {
        setLoading(true);
        const profileResponse = await fetchAmbulanceProfile(vendorId);
        setAmbulanceData(profileResponse);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId) return;
    loadBookings(currentPage);
  }, [vendorId, currentPage, searchText, statusFilter, loadBookings]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, statusFilter]);

  useEffect(() => {
    if (!vendorId) return;
    Promise.all([
      fetchVendorFinancialOverview(vendorId).then(setFinancialOverview).catch(() => setFinancialOverview(null)),
      fetchLocationAnalytics(vendorId).then(setLocationAnalytics).catch(() => setLocationAnalytics(null)),
      fetchVehicleUsage(vendorId).then(setVehicleUsage).catch(() => setVehicleUsage(null)),
      getFeedbacks({ serviceType: 'AMBULANCE', targetType: 'VENDOR', targetId: vendorId, limit: 10 }).then((r) => setVendorFeedback(r.data || [])).catch(() => setVendorFeedback([])),
    ]);
  }, [vendorId]);

  const totalPages = pagination?.totalPages ?? 1;
  const totalBookings = pagination?.total ?? 0;

  const handleToggleStatus = async (vendorId: string, currentStatus: string) => {
    try {
      setUpdatingStatus(vendorId);
      const response = await toggleVendorStatus(vendorId);
      if (response.success) {
        setAmbulanceData((prev: AmbulanceVendor | null) => {
          if (!prev) return null;
          return {
            ...prev,
            status: response.data.status as 'approved' | 'pending' | 'rejected'
          };
        });
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      setError('Failed to toggle status. Please try again later.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const openCommissionModal = async () => {
    if (!vendorId) return;
    setIsCommissionModalOpen(true);
    setCommissionError(null);
    setCommissionLoading(true);
    try {
      const response = await fetchVendorCommissionRate(vendorId);
      setCommissionRateInput(String(response?.data?.commissionRate ?? 0));
      setReferralCommissionInput(String(response?.data?.referralCommission ?? 0));
    } catch (error: any) {
      setCommissionError(error?.response?.data?.message || 'Failed to load current commission rate');
      setCommissionRateInput('');
      setReferralCommissionInput('');
    } finally {
      setCommissionLoading(false);
    }
  };

  const closeCommissionModal = () => {
    if (commissionSaving) return;
    setIsCommissionModalOpen(false);
    setCommissionError(null);
    setCommissionRateInput('');
    setReferralCommissionInput('');
    setCommissionLoading(false);
  };

  const handleSaveCommissionRate = async () => {
    if (!vendorId) return;
    const parsed = Number(commissionRateInput);
    const referralParsed = Number(referralCommissionInput);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
      setCommissionError('Please enter a valid commission rate between 0 and 100.');
      return;
    }
    if (Number.isNaN(referralParsed) || referralParsed < 0) {
      setCommissionError('Please enter a valid referral commission amount (0 or more).');
      return;
    }
    const confirmed = window.confirm(
      `Are you sure you want to update commission rate to ${parsed.toFixed(2)}% and referral commission to ₹${referralParsed.toFixed(2)} for ${profile.agencyName}?`
    );
    if (!confirmed) return;

    try {
      setCommissionSaving(true);
      setCommissionError(null);
      await updateVendorCommissionRate(vendorId, {
        commissionRate: parsed,
        referralCommission: referralParsed,
      });
      setAmbulanceData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          AmbulanceAgencyProfile: {
            ...prev.AmbulanceAgencyProfile,
            commissionRate: parsed,
          },
        };
      });
      closeCommissionModal();
    } catch (error: any) {
      setCommissionError(error?.response?.data?.message || 'Failed to update commission rate');
    } finally {
      setCommissionSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !ambulanceData) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 dark:text-red-400">{error || 'Ambulance data not found'}</p>
      </div>
    );
  }

  const { AmbulanceAgencyProfile: profile } = ambulanceData;

  return (
    <div className="w-full min-w-0 max-w-7xl mx-auto p-3 sm:p-4 md:p-6">
      {/* Header with Ambulance Agency Info */}
      <div className="mb-8 bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start space-x-4 min-w-0 flex-1">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg shrink-0 mt-1">
                <TruckIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate">{profile.agencyName}</h1>
                <div className="flex flex-col space-y-1.5 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4 mt-2">
                  <div className="flex items-center text-gray-600 dark:text-gray-400 min-w-0">
                    <MapPinIcon className="h-4 w-4 mr-1 shrink-0" />
                    <span className="text-sm truncate">
                      {[profile.address, profile.city, profile.state].filter(val => val && val.trim() !== '').join(', ') || '—'}
                    </span>
                  </div>
                  <div className="flex items-center text-gray-600 dark:text-gray-400 shrink-0">
                    <PhoneIcon className="h-4 w-4 mr-1 shrink-0" />
                    <span className="text-sm">{profile.contactNumber || '—'}</span>
                  </div>
                  <div className="flex items-center text-gray-600 dark:text-gray-400 min-w-0">
                    <EnvelopeIcon className="h-4 w-4 mr-1 shrink-0" />
                    <span className="text-sm truncate">{profile.email || '—'}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end shrink-0">
              <div className="relative flex items-center">
              <button
                onClick={() => setHeaderMenuOpen((o) => !o)}
                className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Actions"
                aria-expanded={headerMenuOpen}
                aria-haspopup="true"
              >
                <EllipsisVerticalIcon className="h-6 w-6" />
              </button>
              {headerMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    aria-hidden="true"
                    onClick={() => setHeaderMenuOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-20 w-56 py-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => { navigate(`/vendors/ambulance/${vendorId}/details`); setHeaderMenuOpen(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <EyeIcon className="h-4 w-4" />
                      View Details
                    </button>
                    <button
                      onClick={() => {
                        setIsBankDetailsModalOpen(true);
                        setHeaderMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center gap-2"
                    >
                      <TagIcon className="h-4 w-4" />
                      Bank Details
                    </button>
                    <button
                      onClick={() => {
                        setIsDocumentUploadOpen(true);
                        setHeaderMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-sky-700 dark:text-sky-300 hover:bg-sky-50 dark:hover:bg-sky-900/20 flex items-center gap-2"
                    >
                      <DocumentTextIcon className="h-4 w-4" />
                      Upload documents (PDF)
                    </button>
                    <button
                      onClick={() => {
                        navigate(`/vendors/ambulance/${vendorId}/pricing`);
                        setHeaderMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-900/20 flex items-center gap-2"
                    >
                      <TableCellsIcon className="h-4 w-4" />
                      Manage Pricing
                    </button>
                    <button
                      onClick={() => {
                        navigate(`/vendors/ambulance/${vendorId}/service-areas`);
                        setHeaderMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 flex items-center gap-2"
                    >
                      <MapPinIcon className="h-4 w-4" />
                      Service areas (pincodes)
                    </button>
                    <button
                      onClick={() => {
                        navigate(`/vendors/ambulance/${vendorId}/fleet`);
                        setHeaderMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-sky-700 dark:text-sky-300 hover:bg-sky-50 dark:hover:bg-sky-900/20 flex items-center gap-2"
                    >
                      <UserGroupIcon className="h-4 w-4" />
                      Manage drivers &amp; ambulances
                    </button>
                    <button
                      onClick={() => {
                        openCommissionModal();
                        setHeaderMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 flex items-center gap-2"
                    >
                      <TagIcon className="h-4 w-4" />
                      Commission Rate
                    </button>
                    <button
                      onClick={() => {
                        handleToggleStatus(ambulanceData.vendorId, ambulanceData.status);
                        setHeaderMenuOpen(false);
                      }}
                      disabled={updatingStatus === ambulanceData.vendorId}
                      className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                        ambulanceData.status === 'approved'
                          ? 'text-yellow-700 dark:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
                          : 'text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20'
                      }`}
                    >
                      {updatingStatus === ambulanceData.vendorId ? (
                        <>
                          <ArrowPathIcon className="h-4 w-4 animate-spin" />
                          Updating...
                        </>
                      ) : ambulanceData.status === 'approved' ? (
                        <>
                          <ClockIcon className="h-4 w-4" />
                          Mark as Pending
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="h-4 w-4" />
                          Mark as Approve
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 pt-4 border-t dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Account status:</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                ambulanceData.status === 'approved'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : ambulanceData.status === 'pending'
                  ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
              }`}>
                {ambulanceData.status.charAt(0).toUpperCase() + ambulanceData.status.slice(1)}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-3">
            <button
              type="button"
              onClick={() => navigate(`/vendors/ambulance/${vendorId}/event-timeline`)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <RectangleStackIcon className="h-5 w-5 text-teal-600 dark:text-teal-400" />
              Event timeline
            </button>
            <button
              type="button"
              onClick={() => navigate(`/vendors/ambulance/${vendorId}/login-activity`)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <ShieldCheckIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              Login activity
            </button>
          </div>
        </div>
      </div>

      {/* Core Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="Total Bookings"
          value={bookingStats?.total || 0}
          icon={<CalendarIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />}
          description="All time bookings"
        />
        <MetricCard
          title="Completed Trips"
          value={bookingStats?.completed || 0}
          icon={<CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />}
          description="Successfully completed"
        />
        <MetricCard
          title="Cancellations"
          value={bookingStats?.cancelled || 0}
          icon={<XCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />}
          description="Cancelled bookings"
        />
        <MetricCard
          title="Pending Bookings"
          value={bookingStats?.pending || 0}
          icon={<ClockIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />}
          description="Awaiting completion"
        />
      </div>

      {/* Bookings Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700 mb-8">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Recent Bookings</h2>
          
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search bookings..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md pl-8 pr-3 py-1.5 text-sm w-48 md:w-64 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-1.5 text-sm"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Booking ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Locations
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {bookingsLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : (
                bookings.map((booking) => {
                  const id = booking.bookingId ?? booking.requestId ?? '';
                  const normalizedStatus = String(booking.status || '').toLowerCase().replace(/\s+/g, '_');
                  const canViewInvoice = AMBULANCE_INVOICE_ELIGIBLE_STATUSES.has(normalizedStatus);
                  return (
                    <tr
                      key={id}
                      onClick={() => id && vendorId && navigate(`/vendors/ambulance/${vendorId}/booking/${id}`)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-xs font-mono text-gray-600 dark:text-gray-300 uppercase">
                          {id ? `${id.slice(0, 8)}…` : '—'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">{booking.user?.name ?? '—'}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{booking.user?.phone_number ?? '—'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          <div className="flex items-center">
                            <MapPinIcon className="h-4 w-4 mr-1 text-gray-400 shrink-0" />
                            <span className="truncate max-w-[140px]">{booking.pickupLocation || '—'}</span>
                          </div>
                          <div className="flex items-center mt-1">
                            <MapPinIcon className="h-4 w-4 mr-1 text-gray-400 shrink-0" />
                            <span className="truncate max-w-[140px]">{booking.dropLocation || '—'}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        ₹{booking.totalPayableAmount ?? booking.totalAmount ?? 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          booking.status === 'Completed'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                            : booking.status === 'Cancelled'
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                            : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                        }`}>
                          {booking.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {booking.createdAt ? format(new Date(booking.createdAt), 'dd MMM yyyy HH:mm') : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {id && canViewInvoice ? (
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const invoiceBlob = await fetchAmbulanceInvoiceBlob(id);
                                const blobUrl = URL.createObjectURL(invoiceBlob);
                                window.open(blobUrl, '_blank', 'noopener,noreferrer');
                                window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                              } catch (err) {
                                console.error('Failed to open ambulance invoice PDF:', err);
                                window.alert('Failed to open invoice. Please try again.');
                              }
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 dark:border-blue-800 dark:text-blue-300 dark:bg-blue-900/20 dark:hover:bg-blue-900/30"
                          >
                            <DocumentTextIcon className="h-4 w-4" />
                            View Invoice
                          </button>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Showing {totalBookings === 0 ? 0 : (currentPage - 1) * 10 + 1} to {Math.min(currentPage * 10, totalBookings)} of {totalBookings} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || bookingsLoading}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <div className="flex items-center space-x-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  disabled={bookingsLoading}
                  className={`px-3 py-1 text-sm rounded-md ${
                    currentPage === page
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage >= totalPages || bookingsLoading}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Response Analytics */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Response Analytics</h2>
            <div className="py-8 text-center text-gray-500 dark:text-gray-400">
              Coming soon
            </div>
          </div>

          {/* Vehicle Usage Stats */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Vehicle Usage Stats</h2>
            <div className="space-y-4">
              {(vehicleUsage?.byType && vehicleUsage.byType.length > 0) ? (
                vehicleUsage.byType.map((v, i) => {
                  const colors = ['bg-blue-500', 'bg-green-500', 'bg-red-500', 'bg-purple-500', 'bg-yellow-500'];
                  return (
                    <div key={v.type} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{v.type.replace(/_/g, ' ')}</span>
                      <div className="flex items-center">
                        <div className="w-48 h-2 bg-gray-200 dark:bg-gray-600 rounded-full mr-2">
                          <div
                            className={`h-2 ${colors[i % colors.length]} rounded-full`}
                            style={{ width: `${v.percentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{v.percentage}%</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No usage data yet.</p>
              )}
            </div>
          </div>

          {/* Location Analytics */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Location Analytics</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">Top Pickup Locations</h3>
                <div className="space-y-3">
                  {(locationAnalytics?.topPickupLocations && locationAnalytics.topPickupLocations.length > 0) ? (
                    locationAnalytics.topPickupLocations.map((loc, idx) => (
                      <div key={`p-${idx}`} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400 truncate max-w-[120px]" title={loc.location}>{loc.location}</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{loc.count}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No data yet.</p>
                  )}
                </div>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">Top Drop-off Locations</h3>
                <div className="space-y-3">
                  {(locationAnalytics?.topDropLocations && locationAnalytics.topDropLocations.length > 0) ? (
                    locationAnalytics.topDropLocations.map((loc, idx) => (
                      <div key={`d-${idx}`} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400 truncate max-w-[120px]" title={loc.location}>{loc.location}</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{loc.count}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No data yet.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Financial Overview */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Financial Overview</h2>
            <div className="space-y-4">
              {financialOverview ? (
                <>
                  {typeof (financialOverview as Record<string, unknown>).pendingSettlement === 'number' && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex items-center">
                        <CurrencyDollarIcon className="h-5 w-5 text-green-500 dark:text-green-400 mr-2" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Pending Settlement</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        ₹{((financialOverview as Record<string, unknown>).pendingSettlement as number).toLocaleString('en-IN')}
                      </span>
                    </div>
                  )}
                  {typeof (financialOverview as Record<string, unknown>).onHold === 'number' && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex items-center">
                        <TagIcon className="h-5 w-5 text-yellow-500 dark:text-yellow-400 mr-2" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">On Hold</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        ₹{((financialOverview as Record<string, unknown>).onHold as number).toLocaleString('en-IN')}
                      </span>
                    </div>
                  )}
                  {(financialOverview.totalPaidOut != null || (financialOverview as Record<string, unknown>).totalPaidOut != null) && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex items-center">
                        <CalculatorIcon className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-2" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Total Paid Out</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        ₹{(Number((financialOverview as Record<string, unknown>).totalPaidOut ?? financialOverview.totalPaidOut ?? 0)).toLocaleString('en-IN')}
                      </span>
                    </div>
                  )}
                  {((financialOverview as Record<string, unknown>).refunds as { totalRefundAmount?: number } | undefined)?.totalRefundAmount != null && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex items-center">
                        <ArrowUturnLeftIcon className="h-5 w-5 text-red-500 dark:text-red-400 mr-2" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Refunds</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        ₹{((financialOverview as Record<string, unknown>).refunds as { totalRefundAmount: number }).totalRefundAmount.toLocaleString('en-IN')}
                      </span>
                    </div>
                  )}
                  {typeof (financialOverview as Record<string, unknown>).totalEarnings === 'number' && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 dark:text-green-400 mr-2" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Total Earnings</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        ₹{((financialOverview as Record<string, unknown>).totalEarnings as number).toLocaleString('en-IN')}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No financial data for this vendor.</p>
              )}
            </div>
          </div>

          {/* User Feedback */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">User Feedback</h2>
            <div className="space-y-4">
              {vendorFeedback.length > 0 ? (
                <>
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="flex items-center">
                      <StarIcon className="h-5 w-5 text-yellow-500 dark:text-yellow-400 mr-2" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Average Rating</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {(vendorFeedback.reduce((s, f) => s + f.rating, 0) / vendorFeedback.length).toFixed(1)}/5
                    </span>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {vendorFeedback.slice(0, 5).map((f) => (
                      <div key={f.feedbackId} className="p-2 bg-gray-50 dark:bg-gray-700/50 rounded text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-700 dark:text-gray-300">{f.submitterName ?? '—'}</span>
                          <span className="text-yellow-600 dark:text-yellow-400">{f.rating}/5</span>
                        </div>
                        {f.review && <p className="text-gray-600 dark:text-gray-400 mt-1 truncate">{f.review}</p>}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No feedback for this vendor yet.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {isCommissionModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center overflow-y-auto bg-black/50 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-5 border-b dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Commission Rate</h3>
              <button onClick={closeCommissionModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                ×
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">{profile.agencyName}</p>
              {commissionLoading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading current rate...</p>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Commission Rate (%)</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step="0.01"
                      value={commissionRateInput}
                      onChange={(e) => setCommissionRateInput(e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Referral Commission Amount (INR)</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={referralCommissionInput}
                      onChange={(e) => setReferralCommissionInput(e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </>
              )}
              {commissionError && <p className="text-sm text-red-600 dark:text-red-400">{commissionError}</p>}
            </div>
            <div className="px-5 py-4 border-t dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={closeCommissionModal}
                disabled={commissionSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCommissionRate}
                disabled={commissionLoading || commissionSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-60"
              >
                {commissionSaving ? 'Saving...' : 'Add / Update'}
              </button>
            </div>
          </div>
        </div>
      )}
      <VendorBankDetailsModal
        vendorId={vendorId || ''}
        vendorName={profile?.agencyName}
        isOpen={isBankDetailsModalOpen}
        onClose={() => setIsBankDetailsModalOpen(false)}
      />
      <AmbulanceVendorDocumentUploadModal
        open={isDocumentUploadOpen}
        vendorId={vendorId || ''}
        onClose={() => setIsDocumentUploadOpen(false)}
        onUploaded={(doc: VendorDocument) => {
          setAmbulanceData((prev) => {
            if (!prev) return prev;
            const current = Array.isArray(prev.vendorDocuments) ? prev.vendorDocuments : [];
            return { ...prev, vendorDocuments: [doc, ...current] };
          });
          toast.success('Document uploaded successfully.');
        }}
      />
    </div>
  );
};

export default AmbulanceVendorDashboard; 